<p-toast></p-toast>

<div class="int-page">

  <!-- Header -->
  <div class="int-header">
    <h1 class="int-title">File Settings</h1>
    <div class="switch-tabs">
      <button type="button" [class.active]="activeTab === 'folders'" (click)="activeTab = 'folders'">
        Folder Templates
      </button>
      <button type="button" [class.active]="activeTab === 'permissions'" (click)="activeTab = 'permissions'">
        Permissions
      </button>
    </div>
  </div>

  <!-- Tab: Folder Templates -->
  @if (activeTab === 'folders') {
    <div class="int-grid-wrap">

      <!-- Panel Header -->
      <div class="int-panel-hdr">
        <p class="int-panel-desc">Create default folder names that can be assigned to project roles.</p>
        <div class="int-panel-actions">
          <input
            class="int-input fs-name-input"
            type="text"
            placeholder="Enter folder name..."
            [(ngModel)]="newFolderName"
            (keyup.enter)="addFolder()"
            [disabled]="addingFolder" />
          <button
            class="int-btn-save"
            type="button"
            (click)="addFolder()"
            [disabled]="addingFolder || !newFolderName.trim()">
            <i class="ri-add-line"></i>
            {{ addingFolder ? 'Adding...' : 'Add Folder' }}
          </button>
        </div>
      </div>

      <!-- Grid Header -->
      <div class="int-grid-header fs-folders-grid">
        <div>Folder Name</div>
        <div>Action</div>
      </div>

      <!-- Grid Body -->
      <div class="int-grid-body">
        @if (!loading && folders.length === 0) {
          <div class="fs-empty-row">
            <i class="ri-folder-open-line"></i>
            No folders created yet. Add your first folder above.
          </div>
        }
        @if (loading) {
          <div class="fs-empty-row">
            <i class="ri-loader-4-line fs-spin"></i>
            Loading...
          </div>
        }
        @for (folder of folders; track folder.id) {
          <div class="int-grid-row fs-folders-grid">
            <div class="fs-folder-cell">
              <i class="ri-folder-fill fs-folder-icon"></i>
              <span>{{ folder.folderName }}</span>
            </div>
            <div>
              <div class="int-action-cell">
                <button
                  type="button"
                  class="int-icon-btn int-icon-btn--delete"
                  [title]="folder.hasAssignments ? 'Cannot delete: folder is assigned to a project' : 'Delete folder'"
                  [disabled]="deletingId === folder.id || folder.hasAssignments"
                  (click)="deleteFolder(folder)">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          </div>
        }
      </div>

    </div>
  }

  <!-- Tab: Permissions -->
  @if (activeTab === 'permissions') {
    <div class="int-grid-wrap">

      <!-- Panel Header -->
      <div class="int-panel-hdr">
        <p class="int-panel-desc">Select a project and assign folders to roles. Users will see their assigned folders in Files.</p>
        <div class="int-panel-actions">
          <p-select
            [options]="projects"
            [(ngModel)]="selectedProject"
            optionLabel="projectName"
            placeholder="Select Project"
            [filter]="true"
            filterBy="projectName"
            [loading]="loading"
            (onChange)="onProjectChange()"
            appendTo="body"
            styleClass="int-template-select">
          </p-select>
          <button
            class="int-btn-save"
            type="button"
            [disabled]="saving || !selectedProject || loadingAssignments"
            (click)="saveAssignments()">
            <i class="ri-save-line"></i>
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>

      @if (roles.length > 0) {
        <div class="fs-permission-card">
          <div class="fs-matrix-header">
            <div class="fs-matrix-title">
              <span class="fs-matrix-name">Folder Permissions</span>
              <span class="fs-matrix-key">FILES_REPOSITORY</span>
            </div>
            <div class="fs-role-cols">
              @for (role of roles; track role.roleId) {
                <div class="fs-role-col">
                  <i class="ri-user-line"></i>
                  <span class="fs-role-name">{{ role.roleName }}</span>
                  <p-checkbox
                    [binary]="true"
                    [ngModel]="isRoleAllFoldersChecked(role.roleId)"
                    [indeterminate]="isRoleAllFoldersIndeterminate(role.roleId)"
                    (ngModelChange)="toggleRoleAllFolders(role.roleId, $event)"
                    [disabled]="!folders.length"
                    [inputId]="'fs-role-all-' + role.roleId"
                    [attr.aria-label]="'Toggle all folders for ' + role.roleName">
                  </p-checkbox>
                </div>
              }
            </div>
          </div>

          <div class="fs-matrix-body">
            @if (loading || loadingAssignments) {
              <div class="fs-empty-row">
                <i class="ri-loader-4-line fs-spin"></i>
                Loading...
              </div>
            }

            @if (!loading && !loadingAssignments && !selectedProject) {
              <div class="fs-empty-row">
                <i class="ri-cursor-line"></i>
                Select a project to manage folder permissions.
              </div>
            }

            @if (!loading && !loadingAssignments && selectedProject && folders.length === 0) {
              <div class="fs-empty-row">
                <i class="ri-folder-open-line"></i>
                No folders created yet. Go to Folder Templates tab to add folders.
              </div>
            }

            @if (!loading && !loadingAssignments && selectedProject && folders.length > 0) {
              @for (folder of folders; track folder.id) {
                <div class="fs-matrix-row">
                  <div class="fs-matrix-folder">
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="isFolderAllRolesChecked(folder.id)"
                      [indeterminate]="isFolderAllRolesIndeterminate(folder.id)"
                      (ngModelChange)="toggleFolderAllRoles(folder.id, $event)"
                      [inputId]="'fs-folder-all-' + folder.id"
                      [attr.aria-label]="'Toggle all roles for ' + folder.folderName">
                    </p-checkbox>
                    <i class="ri-folder-fill fs-folder-icon"></i>
                    <span>{{ folder.folderName }}</span>
                  </div>
                  <div class="fs-role-cols">
                    @for (role of roles; track role.roleId) {
                      <div class="fs-check-cell">
                        <p-checkbox
                          [binary]="true"
                          [ngModel]="isAssigned(folder.id, role.roleId)"
                          (ngModelChange)="toggleAssignment(folder.id, role.roleId, $event)">
                        </p-checkbox>
                      </div>
                    }
                  </div>
                </div>
              }
            }
          </div>
        </div>
      }

      @if (roles.length === 0) {
        <div class="int-grid-body">
          @if (loading || loadingAssignments) {
            <div class="fs-empty-row">
              <i class="ri-loader-4-line fs-spin"></i>
              Loading...
            </div>
          }
          @if (!loading && !loadingAssignments) {
            <div class="fs-empty-row">
              <i class="ri-user-line"></i>
              No project roles configured.
            </div>
          }
        </div>
      }

    </div>
  }

</div>
